import { type FC, useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Button, styled, SvgIconProps } from '@mui/material';
import { denySmartExtraFraction } from '../../utils';
import { KeyboardArrowRight } from '@mui/icons-material';
import { useRequest } from 'ahooks';
import { getNFTItem } from '@/api';
import Tooltip from '@mui/material/Tooltip';
import Link from 'next/link';
import classNames from 'classnames';
import Circle from '@/components/icons/Circle';
import Swift from '@/components/icons/Swift';
import Wormhole from '@/components/icons/Wormhole';
import { useRouter } from 'next/router';
import PulseLoader from 'react-spinners/PulseLoader'

export const Trader: FC<{ address: string, nested?: boolean }> = ({ address, nested = false }) => {
	const displayAddress = useMemo<string>(
		() => (address ? `${address.slice(0, 6)}...${address.slice(address.length - 5)}` : ''),
		[address],
	);
	const router = useRouter();
	return (
		!nested ? (
			<Typography
				className='transition text-textSecondary no-underline underline-offset-4 hover:underline hover:text-textPrimary'
				variant='appSmallRegular'
				component={Link}
				href={`/trader/${address}`}
				onClick={(e) => {
					e.stopPropagation();
				}}
			>
				{displayAddress}
			</Typography>
		) : (
			<Typography
				className='transition text-textSecondary no-underline underline-offset-4 hover:underline hover:text-textPrimary'
				variant='appSmallRegular'
				onClick={(e) => {
					e.preventDefault();
					e.stopPropagation();
					router.push(`/trader/${address}`);
				}}
			>
				{displayAddress}
			</Typography>
		)
	);
};

const Token: FC<{ symbol: string; amount: number | string }> = ({ symbol, amount }) => {
	const displayAmount = useMemo(() => {
		const value = Number(denySmartExtraFraction(amount));
		if (value) {
			return value.toLocaleString('en-US', { maximumFractionDigits: 7 });
		}
		return amount === '-' ? null : <PulseLoader color='#ffffff' size={4} speedMultiplier={0.5} />;
	}, [amount]);
	return (
		<Typography variant='appSmallRegular' color='common.textPrimary'>
			{displayAmount || '-'}&nbsp;{symbol}
		</Typography>
	);
};

export function useServiceInfo(service: TradeListItemResponse['service']) {
	return useMemo<{
		title: string;
		icon: FC<SvgIconProps>;
	} | null>(() => {
		if (['SWIFT_NFT', 'SWIFT_SWAP', 'SWIFT_V2'].includes(service)) {
			return {
				title: 'Swift',
				icon: Swift,
			};
		}
		if (service === 'MCTP_SWAP' || service === 'MCTP_BRIDGE' || service === 'MCTP_BRIDGE_WITH_UNLOCK') {
			return {
				title: 'MCTP',
				icon: Circle,
			};
		}
		if (service === 'MCTP_FAST_SWAP' || service === 'MCTP_FAST_BRIDGE') {
			return {
				title: 'Fast MCTP',
				icon: Circle,
			};
		}
		if (service === 'WH_BRIDGE' || service === 'WH_SWAP') {
			return {
				title: 'Wormhole',
				icon: Wormhole,
			};
		}
		return null;
	}, [service]);
}


export function ServiceIcon({ service, className }: { service: TradeListItemResponse['service']; className?: string }) {
	const serviceInfo = useServiceInfo(service);

	if (!serviceInfo) {
		return null;
	}

	return (
		<Tooltip
			className={className}
			slotProps={{
				tooltip: { sx: { backgroundColor: 'common.backgroundTertiary', color: 'common.textPrimary', borderRadius: 0, fontSize: 12, fontWeight: 400, padding: '4px 12px' } },
				arrow: { sx: { '&::before': { backgroundColor: 'common.backgroundTertiary' } } },
			}}
			title={serviceInfo.title}
			arrow
		>
			<div className='flex items-center justify-center transition-colors text-textSecondary hover:text-textPrimary'>
				<serviceInfo.icon className='w-4 h-4' />
			</div>
		</Tooltip>
	);
}

export function TokenPair({ row }: { row: TradeListItemResponse }) {
	return (
		<div className='flex items-center gap-4 flex-wrap'>
			<Token symbol={row.fromTokenSymbol} amount={row.fromAmount} />
			<div className='flex items-center justify-center'>
				<KeyboardArrowRight sx={{ fontSize: '1.5rem', color: 'common.textPrimary' }} />
			</div>
			{row.service === 'SWIFT_NFT' ? (
				<NFTItem data={row} />
			) : (
				<Token symbol={row.toTokenSymbol} amount={row.isCanceled || row.isRefunded ? '-' : row.toAmount} />
			)}
		</div>
	);
}

const Icon = styled('img')<{ small?: boolean }>(({ small }) => ({
	width: small ? 16 : 24,
	height: small ? 16 : 24,
	borderRadius: '50%',
}));

export const Chains = ({ chain1, chain3, small }: { chain1: Chain; chain2: Chain; chain3: Chain; small?: boolean }) => {
	return (
		<div className={classNames('flex items-center justify-center', small ? 'gap-1' : 'gap-2')}>
			<Icon small={small} src={chain1.logoURI} title={chain1.chainName} />
			{chain1.chainId !== chain3.chainId && (
				<>
					<KeyboardArrowRight sx={{ fontSize: '1.5rem', color: 'common.textPrimary' }} />
					<Icon small={small} src={chain3.logoURI} title={chain3.chainName} />
				</>
			)}
		</div>
	);
};

export function statusText(data: TradeListItemResponse) {
	return data.isCompleted
		? 'Completed'
		: data.isRefunded
			? 'Refunded'
			: data.isCanceled
				? 'Canceled'
				: 'In progress';
}

const NFTImage = styled('img')({
	width: 43,
	height: 43,
	borderRadius: 8,
});
const NFTItem: FC<{ data: TradeListItemResponse }> = ({ data }) => {
	const { data: item } = useRequest(() => getNFTItem(data.toTokenAddress));
	const { name, subName } = useMemo(() => {
		const raw = data.toTokenSymbol;
		if (!raw) {
			return {
				name: null,
				subName: null,
			};
		}
		if (raw.indexOf('#') >= 0) {
			const _name = raw;
			return {
				name: _name.slice(0, _name.indexOf('#')),
				subName: _name.slice(_name.indexOf('#') + 1),
			};
		}
		return {
			name: raw,
			subName: null,
		};
	}, [data?.toTokenSymbol]);
	return (
		<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
			<Box sx={{ width: 43, height: 43, mr: 1.5 }}>
				{item?.imageUrl && <NFTImage src={item.imageUrl} title={data?.toTokenSymbol} />}
			</Box>
			<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
				<Typography variant='appMediumBold' color='common.N250'>
					{name}
				</Typography>
				{subName && (
					<Typography sx={{ mt: -1 }} variant='appCaptionRegular' color='common.N350'>
						#{subName}
					</Typography>
				)}
			</Box>
		</Box>
	);
};
// export const columns: GridColDef[] = [
// 	{
// 		field: 'age',
// 		headerName: 'Age',
// 		sortable: false,
// 		flex: 1,
// 		minWidth: 120,
// 		renderCell: ({ row }: { row: TradeListItemResponse }) => <Age time={row.initiatedAt} />,
// 	},
// 	{
// 		field: 'trader',
// 		headerName: 'Trader',
// 		sortable: false,
// 		flex: 1,
// 		minWidth: 100,
// 		renderCell: ({ row }: { row: TradeListItemResponse }) => (
// 			<Container>
// 				<Trader address={row.trader} />
// 			</Container>
// 		),
// 	},
// 	{
// 		field: 'fromToken',
// 		headerName: 'Token',
// 		sortable: false,
// 		minWidth: 300,
// 		flex: 1,
// 		align: 'center',
// 		headerAlign: 'center',
// 		renderCell: TokenPair,
// 	},
// 	{
// 		field: 'chains',
// 		headerName: 'Chains',
// 		sortable: false,
// 		flex: 1,
// 		align: 'center',
// 		minWidth: 150,
// 		renderCell: ({ row }: { row: TradeListItemResponse }) => (
// 			<Chains
// 				chain1={row.sourceChain}
// 				chain2={row.service === 'WH_BRIDGE' ? row.sourceChain : row.swapChain}
// 				chain3={row.destChain}
// 			/>
// 		),
// 	},
// 	{
// 		field: 'past30DaysNavPerformance',
// 		headerName: 'Status',
// 		sortable: false,
// 		flex: 1,
// 		minWidth: 100,
// 		renderCell: ({ row }: { row: TradeListItemResponse }) => <Status data={row} />,
// 	},
// ];
